import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useShowApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from 'utils/network/axios'
import { PagingResponse } from 'entities'
import { SalesAgency, SalesAgencyForm, SalesAgencySearchForm } from 'entities/sales_agency'

type SalesAgenciesResponse = PagingResponse & {
    results: SalesAgency[]
}

export function useFetchSalesAgenciesApi(searchForm?: Form<SalesAgencySearchForm>): IndexApiSet<SalesAgenciesResponse> & { execute: () => void } {
    const apiPath = 'sales_agencies/'
    const api = useIndexApi<SalesAgenciesResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object?.page || 1, perPage: searchForm?.object?.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function useFetchSalesAgencyApi(): ApiSet<BaseResponse> & { execute: (id: number) => void } {
    const apiSet = useShowApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number) => {
        const apiPath = `sales_agencies/${id}/`
        apiSet.execute(apiPath)
    }

    return { ...apiSet, execute: execute }
}

export function usePostSalesAgencyApi(): ApiSet<BaseResponse> & { execute: (form: Form<SalesAgencyForm>) => void } {
    const apiSet = usePostApi<BaseResponse, SalesAgencyForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<SalesAgencyForm>) => {
        const apiPath = `sales_agencies/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutSalesAgencyApi(): ApiSet<BaseResponse> & { execute: (form: Form<SalesAgencyForm>) => void } {
    const apiSet = usePutApi<BaseResponse, SalesAgencyForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<SalesAgencyForm>) => {
        const apiPath = `sales_agencies/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutSalesAgenciesApi(): ApiSet<BaseResponse> & { execute: (form: Form<SalesAgencyForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, SalesAgencyForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<SalesAgencyForm[]>) => {
        const apiPath = `sales_agencies/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteSalesAgencyApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `sales_agencies/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
